import { Form, FormEvents } from "@dhx/ts-form";
import { SpreadsheetEvents } from "../types";
import { IEventSystem } from "@dhx/ts-common/events";
import { Window } from "@dhx/ts-window";
import locale from "../locales/en";
import { PopupEvents } from "@dhx/ts-popup";
import { getCellIds } from "../helpers/main";
import { IGrid } from "@dhx/ts-grid";

export class ValidationForm {
	protected _handlers: { [key: string]: (...args) => void };
	private _form: Form;
	private _modal: Window;
	private _focus: string;
	private _events: IEventSystem<SpreadsheetEvents>;
	private _set: (cell: string, options: string[]) => void;
	private _grid: IGrid;

	constructor(events, set, grid: IGrid) {
		this._set = set;
		this._grid = grid;
		this._modal = new Window({
			title: locale.validation,
			movable: true,
			closable: true,
			css: "dhx-spreadsheet-form-validation",
			height: 250,
			width: 600,
		});
		this._form = new Form(null, {
			rows: [
				{
					name: "cell",
					type: "input",
					label: "Cell range:",
					labelPosition: "left",
					labelWidth: 70,
					width: 270,
					placeholder: "Enter cell range",
				},
				{
					cols: [
						{
							name: "criteria",
							type: "combo",
							label: "Criteria:",
							labelPosition: "left",
							labelWidth: 70,
							width: 270,
							readonly: true,
							multiselection: false,
							data: [
								{ value: "List of items", id: "items" },
								{ value: "List from a range", id: "range" },
							],
						},
						{
							labelWidth: 3,
							width: 260,
							labelPosition: "left",
							name: "target",
							type: "input",
							validation: this.formValidation.bind(this),
							errorMessage: "Invalid cell range",
							placeholder: "Enter cell range",
						},
					],
				},
				{
					align: "end",
					cols: [
						{
							type: "button",
							text: locale.cancel,
							id: "cancel",
							view: "link",
						},
						{
							type: "button",
							text: locale.removeValidation,
							id: "remove",
							view: "link",
						},
						{
							type: "button",
							text: locale.save,
							id: "save",
						},
					],
				},
			],
		});
		this._modal.attach(this._form);
		this._events = events;
		this._initHandlers();
	}

	hide() {
		if (this._modal.isVisible()) {
			this._modal.hide();
		}
		this._events.fire(SpreadsheetEvents.afterPopupHide);
	}

	formValidation(val) {
		if (!val) return true;
		const criteria = this._form.getItem("criteria").getValue();
		if (criteria === "range") {
			const { row, start } = getCellIds(val, this._grid);
			return row || start;
		}
		return true;
	}

	show(cell: string, config?: any) {
		if (!this._modal.isVisible()) {
			this._modal.show();
		}
		const conf = {...config};
		if (config.editor) {
			const options = config.editor.options;
			conf.target = typeof options === "string" ? options : options.join(",");
			conf.criteria = typeof options === "string" ? "range" : "items";
		}
		this._form.setValue({
			cell,
			criteria: "items",
			target: "",
			...conf,
		});
	}
	protected _save(save: boolean) {
		let { target, cell, criteria } = this._form.getValue();
		if (!this._form.validate()) {
			this._form.blur("target");
			return;
		}
		if (save && !target) return;
		const isRange = criteria === "range";
		if (isRange && !getCellIds(target, this._grid).start) {
			target += `:${target}`;
		}
		this.hide();
		const options = isRange ? target : target.split(",").map(i => i.trim());
		this._set(cell, save ? options : null);
	}

	protected _initHandlers() {
		this._form.events.on(FormEvents.focus, name => {
			this._focus = name;
		});
		this._modal.events.on(PopupEvents.afterShow, () => {
			this._form.setFocus("target");
		});
		this._form.events.on(FormEvents.click, name => {
			switch(name) {
				case "save":
					this._save(true);
					break;
				case "remove":
					this._save(false);
					break;
				default: 
					this.hide();
			}
		});
		this._form.getItem("criteria").events.on(FormEvents.change, val => {
			const placeholder = `Enter ${val === "range" ? "cell range" : " items separated by a comma"}`;
			this._form.getItem("target").setProperties({ placeholder });
			this._form.setFocus("target");
		});
		this._events.on(SpreadsheetEvents.afterSelectionSet, cell => {
			if (this._focus === "cell") {
				this._form.setValue({ cell });
				this._form.setFocus("cell");
			}
			if (this._focus === "target" && this._form.getItem("criteria").getValue() === "range") {
				this._form.setValue({ target: cell });
				this._form.setFocus("target");
			}
		});
	}
}
